import React, { useState, useEffect } from 'react'
import {
  ArrowLeft,
  Save,
  X,
  Instagram,
  Music,
  Facebook,
  Twitter,
  Youtube,
  Linkedin
} from 'lucide-react'
import { Card, Button, Input, Select } from '../ui'
import {
  PlatformAccount,
  UpdatePlatformAccountRequest,
  CreatePlatformAccountRequest
} from '@/types/platformAccount'
import { Customer } from '@/types/customer'
import { PLATFORMS } from '@/types/platformAccount'
import { platformAccountService } from '@/services/platformAccountService'
import {
  platformService,
  Platform,
  PlatformCategory
} from '@/services/platformService'
import { useToast } from '@/contexts/ToastContext'
import { useAuthStore } from '@/stores/authStore'
import { canAccessSupportPage } from '@/utils/permissions'

interface PlatformAccountFormProps {
  customer?: Customer
  account?: PlatformAccount | null
  onCancel: () => void
  onSuccess: (account: PlatformAccount) => void
  isDrawer?: boolean
  activeAccountCount?: number
  maxSocialAccounts?: number
}

const PlatformAccountForm: React.FC<PlatformAccountFormProps> = ({
  customer,
  account,
  onCancel,
  onSuccess,
  isDrawer = false,
  activeAccountCount,
  maxSocialAccounts
}) => {
  const { showError, showSuccess } = useToast()
  const { user } = useAuthStore()
  const isEditing = !!account

  // Check if we can enable this account (only relevant for inactive accounts being edited)
  const isAccountCurrentlyInactive = isEditing && account?.isActive === false
  const currentActiveCount = activeAccountCount ?? 0
  const maxAccounts = maxSocialAccounts ?? Infinity
  const canEnableAccount = !isAccountCurrentlyInactive || currentActiveCount < maxAccounts

  const [loading, setLoading] = useState(false)
  const [platforms, setPlatforms] = useState<Platform[]>([])
  const [loadingPlatforms, setLoadingPlatforms] = useState(true)
  const [categories, setCategories] = useState<PlatformCategory[]>([])
  const [loadingCategories, setLoadingCategories] = useState(true)

  // Check if user can view/edit EMV rates (only support/system_admin)
  const canEditEmvRates = canAccessSupportPage(user)

  const [formData, setFormData] = useState({
    customerId: account?.customerId || customer?.id || '',
    platformId: account?.platformId || PLATFORMS.INSTAGRAM,
    categoryId: account?.categoryId || 1, // Default to first category (Owned)
    username: account?.username || '',
    displayName: account?.displayName || '',
    profileUrl: account?.profileUrl || '',
    followerCount: account?.followerCount || 0,
    followingCount: account?.followingCount || 0,
    totalVideos: account?.totalVideos || 0,
    verified: account?.verified || false,
    isActive: account?.isActive !== false, // Default to true
    emvViewRate: account?.emvViewRate || 0.01,
    emvLikeRate: account?.emvLikeRate || 0.04,
    emvCommentRate: account?.emvCommentRate || 0.15,
    emvShareRate: account?.emvShareRate || 0.1
  })

  // Track if form has been modified
  const [hasChanges, setHasChanges] = useState(false)
  const originalValues = {
    isActive: account?.isActive !== false,
    emvViewRate: account?.emvViewRate || 0,
    emvLikeRate: account?.emvLikeRate || 0,
    emvCommentRate: account?.emvCommentRate || 0,
    emvShareRate: account?.emvShareRate || 0
  }

  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({})
  const [showEmvWarning, setShowEmvWarning] = useState(false)
  const [emvWarningFields, setEmvWarningFields] = useState<string[]>([])

  // Load available platforms for the dropdown
  useEffect(() => {
    const fetchPlatforms = async () => {
      try {
        setLoadingPlatforms(true)
        const response = await platformService.getPlatforms()
        setPlatforms(response.data.platforms.filter((p) => p.isActive))
      } catch (err) {
        showError(
          'Failed to load platforms',
          err instanceof Error ? err.message : 'An unexpected error occurred'
        )
      } finally {
        setLoadingPlatforms(false)
      }
    }

    fetchPlatforms()
  }, [])

  // Load available categories for the dropdown
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true)
        const response = await platformService.getCategories()
        setCategories(response.data.categories.filter((c) => c.isActive))
      } catch (err) {
        showError(
          'Failed to load categories',
          err instanceof Error ? err.message : 'An unexpected error occurred'
        )
      } finally {
        setLoadingCategories(false)
      }
    }

    fetchCategories()
  }, [])

  const platformOptions = platforms.map((platform) => ({
    value: platform.id.toString(),
    label: platform.platformDisplayName
  }))

  const categoryOptions = categories.map((category) => ({
    value: category.id.toString(),
    label: category.category_name
  }))

  // Add validation for required fields when creating new accounts
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}

    if (!isEditing) {
      if (!formData.username.trim()) {
        errors.username = 'Username is required'
      } else if (formData.username.length < 2) {
        errors.username = 'Username must be at least 2 characters'
      } else if (formData.username.includes('@')) {
        errors.username = 'Username should not contain @ symbol'
      }

      if (!formData.platformId) {
        errors.platformId = 'Platform is required'
      }

      if (!formData.categoryId) {
        errors.categoryId = 'Category is required'
      }
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const validateEmvRates = (): boolean => {
    const errors: Record<string, string> = {}
    const zeroFields = []

    // Check for zero values
    if (formData.emvViewRate === 0) zeroFields.push('View Rate')
    if (formData.emvLikeRate === 0) zeroFields.push('Like Rate')
    if (formData.emvCommentRate === 0) zeroFields.push('Comment Rate')
    if (formData.emvShareRate === 0) zeroFields.push('Share Rate')

    // Check for values exceeding max (99.9999)
    if (formData.emvViewRate > 99.9999) {
      errors.emvViewRate = 'View Rate cannot exceed 99.9999'
    }
    if (formData.emvLikeRate > 99.9999) {
      errors.emvLikeRate = 'Like Rate cannot exceed 99.9999'
    }
    if (formData.emvCommentRate > 99.9999) {
      errors.emvCommentRate = 'Comment Rate cannot exceed 99.9999'
    }
    if (formData.emvShareRate > 99.9999) {
      errors.emvShareRate = 'Share Rate cannot exceed 99.9999'
    }

    // If there are validation errors (exceeding max), show them and block submission
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      return false
    }

    // If there are zero values, show warning but allow user to proceed
    if (zeroFields.length > 0) {
      setEmvWarningFields(zeroFields)
      setShowEmvWarning(true)
      return false
    }

    return true
  }

  const handleEmvWarningConfirm = () => {
    setShowEmvWarning(false)
    proceedWithSubmit()
  }

  const handleEmvWarningCancel = () => {
    setShowEmvWarning(false)
  }

  // Removed unused customerOptions

  const getPlatformIcon = (platformId: number) => {
    const platform = platforms.find((p) => p.id === platformId)
    if (!platform) return Instagram

    const platformName = platform.platformName.toLowerCase()
    switch (platformName) {
      case 'instagram':
        return Instagram
      case 'tiktok':
        return Music
      case 'facebook':
        return Facebook
      case 'twitter':
      case 'x':
        return Twitter
      case 'youtube':
        return Youtube
      case 'linkedin':
        return Linkedin
      default:
        return Instagram
    }
  }

  // No validation needed since we're only updating isActive

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    if (isEditing && !hasChanges) {
      return
    }

    // Check for zero EMV values (only for support/system_admin users who can see EMV fields)
    if (canEditEmvRates && !validateEmvRates()) {
      return
    }

    proceedWithSubmit()
  }

  const proceedWithSubmit = async () => {
    // Check if we're trying to enable an inactive account when at limit
    if (isAccountCurrentlyInactive && formData.isActive && !canEnableAccount) {
      showError(
        'Account Limit Reached',
        `Enabling this account would exceed your plan limit of ${maxAccounts} active social accounts. Please upgrade your plan to add more accounts.`
      )
      return
    }

    setLoading(true)

    try {
      if (isEditing && account) {
        // Update existing platform account
        const updateData: UpdatePlatformAccountRequest = {
          isActive: formData.isActive,
          emvViewRate: formData.emvViewRate,
          emvLikeRate: formData.emvLikeRate,
          emvCommentRate: formData.emvCommentRate,
          emvShareRate: formData.emvShareRate
        }

        const response = await platformAccountService.updatePlatformAccount(
          account.id,
          updateData
        )

        showSuccess(
          'Platform Account Updated',
          `@${formData.username} has been successfully updated.`
        )

        onSuccess(response.data.platformAccount)
        setHasChanges(false)
      } else {
        // Create new platform account
        const createData: CreatePlatformAccountRequest = {
          customerId: customer?.id || '',
          platformId: formData.platformId,
          categoryId: formData.categoryId,
          username: formData.username,
          displayName: formData.displayName || undefined,
          emvViewRate: formData.emvViewRate,
          emvLikeRate: formData.emvLikeRate,
          emvCommentRate: formData.emvCommentRate,
          emvShareRate: formData.emvShareRate,
          isActive: formData.isActive
        }

        const response = await platformAccountService.createPlatformAccount(
          createData
        )

        showSuccess(
          'Platform Account Created',
          `@${formData.username} has been successfully created.`
        )

        onSuccess(response.data.platformAccount)
      }
    } catch (err) {
      showError(
        'Operation Failed',
        err instanceof Error ? err.message : 'Failed to save platform account'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (
    field: string,
    value: string | number | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }))

    // For new accounts, any input should enable the button
    if (!isEditing) {
      setHasChanges(true)
    } else {
      // For editing, check if form has changes for editable fields
      const editableFields = [
        'isActive',
        'emvViewRate',
        'emvLikeRate',
        'emvCommentRate',
        'emvShareRate'
      ] as const
      if (editableFields.includes(field as any)) {
        const newFormData = { ...formData, [field]: value }
        const hasAnyChanges = editableFields.some((f) => {
          const newValue = field === f ? value : newFormData[f]
          return newValue !== originalValues[f]
        })
        setHasChanges(hasAnyChanges)
      }
    }

    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: '' }))
    }
  }

  const PlatformIcon = getPlatformIcon(formData.platformId)

  return (
    <div className='space-y-6'>
      {/* Header - only show when not in drawer */}
      {!isDrawer && (
        <div className='flex items-center justify-between'>
          <div className='flex items-center space-x-4'>
            <Button
              variant='ghost'
              onClick={onCancel}
              className='flex items-center space-x-2'
            >
              <ArrowLeft className='h-4 w-4' />
              <span>Back</span>
            </Button>
            <div>
              <h1 className='text-2xl font-bold text-gray-900'>
                {isEditing
                  ? 'Edit Social Media Account'
                  : 'Add Social Media Account'}
              </h1>
              <p className='text-gray-600'>
                {isEditing
                  ? 'Update social media account information'
                  : 'Connect a new social media account'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card
          variant='elevated'
          className='border-t-4 border-t-blue-500 shadow-lg'
        >
          <div className='space-y-4'>
            {/* Header with Active Account toggle */}
            <div className='flex items-center justify-between mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100'>
              <h3 className='text-base font-medium text-gray-900 flex items-center space-x-2'>
                <PlatformIcon className='h-5 w-5 text-blue-600' />
                <span>Account Information</span>
              </h3>
              <div className='flex flex-col items-end'>
                <div className='flex items-center space-x-2'>
                  <input
                    type='checkbox'
                    id='isActive'
                    checked={formData.isActive}
                    onChange={(e) =>
                      handleInputChange('isActive', e.target.checked)
                    }
                    disabled={!canEnableAccount && !formData.isActive}
                    className='h-3.5 w-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed'
                  />
                  <label
                    htmlFor='isActive'
                    className={`text-sm font-medium ${!canEnableAccount && !formData.isActive ? 'text-gray-400' : 'text-gray-700'}`}
                  >
                    Active Account
                  </label>
                </div>
                {!canEnableAccount && !formData.isActive && (
                  <p className='text-xs text-amber-600 mt-1'>
                    Account limit reached ({currentActiveCount}/{maxAccounts}). Upgrade to enable.
                  </p>
                )}
              </div>
            </div>

            {/* Basic Information */}
            <div>
              {/* Platform - Full Width Row */}
              <div className='mb-4'>
                <div>
                  <label className='block text-xs font-medium text-gray-600 mb-1'>
                    Platform {!isEditing && '*'}
                  </label>
                  {isEditing ? (
                    <div className='px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded text-sm text-gray-900'>
                      {platformOptions.find(
                        (p) => p.value === formData.platformId.toString()
                      )?.label || ''}
                    </div>
                  ) : loadingPlatforms ? (
                    <div className='px-2.5 py-1.5 bg-gray-100 border border-gray-200 rounded text-sm text-gray-500'>
                      Loading platforms...
                    </div>
                  ) : (
                    <Select
                      value={formData.platformId.toString()}
                      onChange={(value: string) =>
                        handleInputChange('platformId', parseInt(value))
                      }
                      options={platformOptions}
                      placeholder='Select a platform'
                      error={validationErrors.platformId}
                      className='text-sm'
                    />
                  )}
                </div>
              </div>

              {/* Username and Category - Two Column Row */}
              <div className='grid grid-cols-1 md:grid-cols-2 gap-3 mb-4'>
                <div>
                  <label className='block text-xs font-medium text-gray-600 mb-1'>
                    Username {!isEditing && '*'}
                  </label>
                  {isEditing ? (
                    <div className='px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded text-sm text-gray-900'>
                      {formData.username ? `@${formData.username}` : 'N/A'}
                    </div>
                  ) : (
                    <Input
                      type='text'
                      value={formData.username}
                      onChange={(e) => {
                        // Remove '@' characters from username input
                        const cleanValue = e.target.value.replace(/@/g, '')
                        handleInputChange('username', cleanValue)
                      }}
                      placeholder='Enter username (without @)'
                      error={validationErrors.username}
                      className='text-sm'
                    />
                  )}
                </div>

                <div>
                  <label className='block text-xs font-medium text-gray-600 mb-1'>
                    Category {!isEditing && '*'}
                  </label>
                  {isEditing ? (
                    <div className='px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded text-sm text-gray-900'>
                      {categoryOptions.find(
                        (c) => c.value === formData.categoryId.toString()
                      )?.label || ''}
                    </div>
                  ) : loadingCategories ? (
                    <div className='px-2.5 py-1.5 bg-gray-100 border border-gray-200 rounded text-sm text-gray-500'>
                      Loading categories...
                    </div>
                  ) : (
                    <Select
                      value={formData.categoryId.toString()}
                      onChange={(value: string) =>
                        handleInputChange('categoryId', parseInt(value))
                      }
                      options={categoryOptions}
                      placeholder='Select a category'
                      error={validationErrors.categoryId}
                      className='text-sm'
                    />
                  )}
                </div>
              </div>

              {/* Display Name and Profile URL - Full Width Rows */}
              <div className='space-y-3'>
                <div>
                  <label className='block text-xs font-medium text-gray-600 mb-1'>
                    Display Name
                  </label>
                  <div className='px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded text-sm text-gray-500'>
                    {formData.displayName ||
                      'Display Name will be Auto Populated'}
                  </div>
                </div>

                <div>
                  <label className='block text-xs font-medium text-gray-600 mb-1'>
                    Profile URL
                  </label>
                  <div className='px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded text-sm text-gray-500 break-all'>
                    {formData.profileUrl ||
                      'Profile URL will be Auto Populated'}
                  </div>
                </div>
              </div>
            </div>

            {/* EMV Rates - Only visible to Support/System Admin */}
            {canEditEmvRates && (
              <div className='bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-100'>
                <h3 className='text-base font-medium text-gray-900 mb-3 flex items-center space-x-2'>
                  <div className='w-2 h-2 bg-purple-500 rounded-full'></div>
                  <span>EMV Rates (USD)</span>
                </h3>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                  <div>
                    <label
                      htmlFor='emvViewRate'
                      className='block text-xs font-medium text-gray-600 mb-1'
                    >
                      View Rate *
                    </label>
                    <input
                      type='number'
                      id='emvViewRate'
                      step='0.0001'
                      min='0'
                      max='99.9999'
                      value={formData.emvViewRate}
                      onChange={(e) =>
                        handleInputChange(
                          'emvViewRate',
                          parseFloat(e.target.value) || 0
                        )
                      }
                      className={`w-full px-2.5 py-1.5 border rounded-lg text-sm text-gray-900 focus:ring-2 transition-all duration-200 ${
                        validationErrors.emvViewRate
                          ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300 focus:ring-purple-500 focus:border-purple-500'
                      }`}
                      placeholder='0.0000'
                    />
                    {validationErrors.emvViewRate && (
                      <p className='mt-1 text-xs text-red-600'>
                        {validationErrors.emvViewRate}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor='emvLikeRate'
                      className='block text-xs font-medium text-gray-600 mb-1'
                    >
                      Like Rate *
                    </label>
                    <input
                      type='number'
                      id='emvLikeRate'
                      step='0.0001'
                      min='0'
                      max='99.9999'
                      value={formData.emvLikeRate}
                      onChange={(e) =>
                        handleInputChange(
                          'emvLikeRate',
                          parseFloat(e.target.value) || 0
                        )
                      }
                      className={`w-full px-2.5 py-1.5 border rounded-lg text-sm text-gray-900 focus:ring-2 transition-all duration-200 ${
                        validationErrors.emvLikeRate
                          ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300 focus:ring-purple-500 focus:border-purple-500'
                      }`}
                      placeholder='0.0000'
                    />
                    {validationErrors.emvLikeRate && (
                      <p className='mt-1 text-xs text-red-600'>
                        {validationErrors.emvLikeRate}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor='emvCommentRate'
                      className='block text-xs font-medium text-gray-600 mb-1'
                    >
                      Comment Rate *
                    </label>
                    <input
                      type='number'
                      id='emvCommentRate'
                      step='0.0001'
                      min='0'
                      max='99.9999'
                      value={formData.emvCommentRate}
                      onChange={(e) =>
                        handleInputChange(
                          'emvCommentRate',
                          parseFloat(e.target.value) || 0
                        )
                      }
                      className={`w-full px-2.5 py-1.5 border rounded-lg text-sm text-gray-900 focus:ring-2 transition-all duration-200 ${
                        validationErrors.emvCommentRate
                          ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300 focus:ring-purple-500 focus:border-purple-500'
                      }`}
                      placeholder='0.0000'
                    />
                    {validationErrors.emvCommentRate && (
                      <p className='mt-1 text-xs text-red-600'>
                        {validationErrors.emvCommentRate}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor='emvShareRate'
                      className='block text-xs font-medium text-gray-600 mb-1'
                    >
                      Share Rate *
                    </label>
                    <input
                      type='number'
                      id='emvShareRate'
                      step='0.0001'
                      min='0'
                      max='99.9999'
                      value={formData.emvShareRate}
                      onChange={(e) =>
                        handleInputChange(
                          'emvShareRate',
                          parseFloat(e.target.value) || 0
                        )
                      }
                      className={`w-full px-2.5 py-1.5 border rounded-lg text-sm text-gray-900 focus:ring-2 transition-all duration-200 ${
                        validationErrors.emvShareRate
                          ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300 focus:ring-purple-500 focus:border-purple-500'
                      }`}
                      placeholder='0.0000'
                    />
                    {validationErrors.emvShareRate && (
                      <p className='mt-1 text-xs text-red-600'>
                        {validationErrors.emvShareRate}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Status */}
            <div className='bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg border border-orange-100'>
              <h3 className='text-base font-medium text-gray-900 mb-3 flex items-center space-x-2'>
                <div className='w-2 h-2 bg-orange-500 rounded-full'></div>
                <span>Account Status</span>
              </h3>
              <div className='mb-2'>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    formData.verified
                      ? 'text-blue-700 bg-blue-100'
                      : 'text-gray-600 bg-gray-200'
                  }`}
                >
                  {formData.verified ? '✓ Verified' : 'Not Verified'}
                </span>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className='flex items-center justify-end space-x-3 pt-4 border-t border-gray-200'>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              onClick={onCancel}
              disabled={loading}
              className='flex items-center space-x-1.5'
            >
              <X className='h-3.5 w-3.5' />
              <span className='text-sm'>Close</span>
            </Button>

            <Button
              type='submit'
              variant='primary'
              size='sm'
              disabled={
                loading ||
                loadingPlatforms ||
                loadingCategories ||
                (isEditing && !hasChanges) ||
                (!isEditing && !formData.username.trim())
              }
              className='flex items-center space-x-1.5'
            >
              {loading ? (
                <div className='animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white'></div>
              ) : (
                <Save className='h-3.5 w-3.5' />
              )}
              <span className='text-sm'>
                {loading
                  ? 'Saving...'
                  : isEditing
                  ? 'Save Changes'
                  : 'Create Account'}
              </span>
            </Button>
          </div>
        </Card>
      </form>

      {/* EMV Warning Modal */}
      {showEmvWarning && (
        <div className='fixed inset-0 z-50 overflow-y-auto'>
          <div className='flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0'>
            {/* Background overlay */}
            <div
              className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity'
              onClick={handleEmvWarningCancel}
            ></div>

            {/* Modal panel */}
            <div className='inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6'>
              <div className='sm:flex sm:items-start'>
                <div className='mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10'>
                  <svg
                    className='h-6 w-6 text-yellow-600'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z'
                    />
                  </svg>
                </div>
                <div className='mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left'>
                  <h3 className='text-lg leading-6 font-medium text-gray-900'>
                    Zero EMV Values Detected
                  </h3>
                  <div className='mt-2'>
                    <p className='text-sm text-gray-500'>
                      The following EMV rates are set to $0.00:
                    </p>
                    <ul className='mt-2 text-sm text-gray-700 list-disc list-inside'>
                      {emvWarningFields.map((field, index) => (
                        <li key={index}>{field}</li>
                      ))}
                    </ul>
                    <p className='mt-3 text-sm text-gray-500'>
                      Are you sure you want to proceed with these zero values?
                      This may affect EMV calculations for this account.
                    </p>
                  </div>
                </div>
              </div>
              <div className='mt-5 sm:mt-4 sm:flex sm:flex-row-reverse'>
                <button
                  type='button'
                  className='w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm transition-colors duration-200'
                  onClick={handleEmvWarningConfirm}
                >
                  Yes, Continue
                </button>
                <button
                  type='button'
                  className='mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm transition-colors duration-200'
                  onClick={handleEmvWarningCancel}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PlatformAccountForm
